import { RootState } from '@/core/redux/store';
import { getData, postData, putData } from '@/services/api.service';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Spinner from './Components/spinner';
import { toast } from 'react-toastify';
import { addCartItem, clearCart } from '@/core/redux/cartSlice';
import { Link, useNavigate, useParams } from 'react-router';
import { useDispatch } from 'react-redux';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';

const OrderDetails = ({ orderId }: { orderId?: number | null }) => {
  const { id: urlOrderId } = useParams<{ id: string }>();
  const [productsList, setProductsList] = useState<
    Array<{
      id: number;
      name: string;
      details: string;
      qty: number;
      unit_price: number;
      total: number;
      image: string;
      is_express?: boolean;
      combo_details?: Array<{
        combo_product_id: number;
        combo_product_name: string;
        price: string;
        quantity: number;
        subtotal: number;
        combo_product: {
          id: number;
          name: string;
          code: string;
          image: string;
          type: string;
          image_path: string;
          image_base_path: string;
        };
      }>;
      product_variant?: {
        variant: {
          name: string;
        };
      };
      rating?: {
        rating: number;
        comment: string;
        status: string;
        created_at: string;
        id?: number;
      };
    }>
  >([]);
  const [salesData, setSalesData] = useState<{
    formatted_date?: string;
    reference_no?: string;
    shipping_cost?: number;
    express_wash_amount?: number;
    coupon_discount?: number;
    order_discount?: number;
    grand_total?: number;
    status?: string;
    id?: number;
  }>({});
  const [loading, setLoading] = useState(true);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [reviewData, setReviewData] = useState({
    rating: 5,
    review: '',
  });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [showSaleReviewModal, setShowSaleReviewModal] = useState(false);
  const [reviewCategories, setReviewCategories] = useState<
    Array<{
      id: number;
      name: string;
      type: string;
      is_active: boolean;
    }>
  >([]);
  const [saleReviewData, setSaleReviewData] = useState({
    comment: '',
    ratings: {} as Record<number, number>,
  });
  const [existingSaleReview, setExistingSaleReview] = useState<any>(null);
  const [submittingSaleReview, setSubmittingSaleReview] = useState(false);
  const [reviewSettings, setReviewSettings] = useState<{
    sale_review_enabled: boolean;
    product_review_enabled: boolean;
  }>({ sale_review_enabled: false, product_review_enabled: false });
  const [expandedCombos, setExpandedCombos] = useState<Set<number>>(new Set());
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userDetails = useSelector(
    (state: RootState) => state.common.userDetails
  );
  const currencyCode = useSelector(
    (state: RootState) => state.common.header?.currency?.symbol
  );

  const getOrderDetails = async () => {
    // Get orderId from prop, URL params, or localStorage (in that order)
    let currentOrderId = orderId || (urlOrderId ? parseInt(urlOrderId) : null);

    if (!currentOrderId) {
      const storedOrderId = localStorage.getItem('selectedOrderId');
      if (storedOrderId) {
        currentOrderId = parseInt(storedOrderId);
        // Update localStorage with the retrieved orderId to ensure consistency
        localStorage.setItem('selectedOrderId', currentOrderId.toString());
      }
    }

    if (!currentOrderId) {
      console.log('No orderId available');
      setLoading(false);
      return;
    }

    setLoading(true);
    const payload = {
      id: currentOrderId,
    };
    try {
      const response = await postData('order_details', payload);
      if (response.status == 200 || response.status == 201) {
        setProductsList(response?.data?.data?.products);
        setSalesData(response?.data?.data?.sale);
        setExistingSaleReview(response?.data?.data?.sale_review);
        setReviewSettings(
          response?.data?.data?.review_settings || {
            sale_review_enabled: false,
            product_review_enabled: false,
          }
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  const repeatOrder = async (orderId: number) => {
    try {
      // Clear existing cart before adding new items
      dispatch(clearCart());

      const response = await postData('repeat_order', { id: orderId });
      if (response.status == 200 || response.status == 201) {
        const orderData = response.data.data;

        // Add each product to the cart
        orderData.products.forEach((product: any) => {
          const cartItem = {
            id: product.id.toString(),
            name: product.name,
            price: product.unit_price,
            image: product.image,
            category_id: '1', // Default category since not provided in API response
            quantity: product.qty,
            service_mode: 'dine_in', // Default service mode
          };

          dispatch(addCartItem(cartItem));
        });

        toast.success(
          `Order repeated! ${orderData.products.length} products added to cart.`
        );

        // Navigate to cart page after adding products
        navigate('/cart');
      }
    } catch (error) {
      console.log(error);
      toast.error('Failed to repeat order');
    }
  };

  const submitReview = async () => {
    if (!selectedProduct || !reviewData.review.trim()) {
      toast.error('Please provide a review');
      return;
    }

    // Get orderId from prop, URL params, or localStorage (in that order)
    let currentOrderId = orderId || (urlOrderId ? parseInt(urlOrderId) : null);

    if (!currentOrderId) {
      const storedOrderId = localStorage.getItem('selectedOrderId');
      if (storedOrderId) {
        currentOrderId = parseInt(storedOrderId);
      }
    }

    if (!currentOrderId) {
      toast.error('Order ID not found');
      return;
    }

    setSubmittingReview(true);
    try {
      let response;

      if (selectedProduct.rating && selectedProduct.rating.id) {
        const payload = {
          rating: reviewData.rating,
          review: reviewData.review,
        };
        response = await putData(
          `reviews/${selectedProduct.rating.id}`,
          payload
        );
      } else {
        const payload = {
          product_id: selectedProduct.id,
          order_id: currentOrderId,
          rating: reviewData.rating,
          review: reviewData.review,
        };
        response = await postData('review_submit', payload);
      }

      if (response.status == 200 || response.status == 201) {
        const successMessage =
          selectedProduct.rating && selectedProduct.rating.id
            ? 'Review updated successfully!'
            : 'Review submitted successfully!';
        toast.success(successMessage);

        setShowReviewModal(false);
        setReviewData({ rating: 5, review: '' });
        setSelectedProduct(null);

        await getOrderDetails();
      }
    } catch (error) {
      console.log('Review submission error:', error);
      const errorMessage = selectedProduct.rating
        ? 'Failed to update review'
        : 'Failed to submit review';
      toast.error(errorMessage);
    } finally {
      setSubmittingReview(false);
    }
  };

  const openReviewModal = (product: any) => {
    setSelectedProduct(product);
    if (product.rating) {
      setReviewData({
        rating: product.rating.rating,
        review: product.rating.comment,
      });
    } else {
      setReviewData({ rating: 5, review: '' });
    }
    setShowReviewModal(true);
  };

  const closeReviewModal = () => {
    setShowReviewModal(false);
    setSelectedProduct(null);
    setReviewData({ rating: 5, review: '' });
  };

  const handleReviewClick = (product: any) => {
    if (salesData?.status !== 'Completed') {
      toast.error('Order must be completed to review this product');
      return;
    }
    openReviewModal(product);
  };

  const getReviewButtonText = (product: any) => {
    if (!product.rating) {
      return '★ Review';
    }
    if (product.rating.status === 'approved') {
      return '✓ Reviewed';
    }
    if (product.rating.status === 'rejected') {
      return '✗ Review Rejected';
    }
    return 'Edit Review';
  };

  const getReviewButtonClass = (product: any) => {
    if (!product.rating) {
      return 'btn btn-sm btn-outline-warning';
    }
    if (product.rating.status === 'approved') {
      return 'btn btn-sm btn-outline-success';
    }
    if (product.rating.status === 'rejected') {
      return 'btn btn-sm btn-outline-secondary';
    }
    return 'btn btn-sm btn-outline-primary';
  };

  const getReviewCategories = async () => {
    try {
      const response = await getData('sale-review-categories', {
        type: 'restaurant',
      });
      if (response.status == 200 || response.status == 201) {
        setReviewCategories(response.data.data);
        // Initialize ratings with default values
        const initialRatings: Record<number, number> = {};
        response.data.data.forEach((category: any) => {
          initialRatings[category.id] = 5;
        });
        setSaleReviewData((prev) => ({ ...prev, ratings: initialRatings }));
      }
    } catch (error) {
      console.log('Error fetching review categories:', error);
    }
  };

  const openSaleReviewModal = () => {
    if (salesData?.status !== 'Completed') {
      toast.error('Order must be completed to review this sale');
      return;
    }

    if (existingSaleReview) {
      // Load existing review data
      const existingRatings: Record<number, number> = {};
      existingSaleReview.ratings.forEach((rating: any) => {
        existingRatings[rating.category.id] = rating.rating;
      });

      setSaleReviewData({
        comment: existingSaleReview.comment,
        ratings: existingRatings,
      });
    } else {
      // Reset for new review
      const initialRatings: Record<number, number> = {};
      reviewCategories.forEach((category: any) => {
        initialRatings[category.id] = 5;
      });
      setSaleReviewData({
        comment: '',
        ratings: initialRatings,
      });
    }
    setShowSaleReviewModal(true);
  };

  const submitSaleReview = async () => {
    let currentOrderId = orderId;
    if (!currentOrderId) {
      const storedOrderId = localStorage.getItem('selectedOrderId');
      if (storedOrderId) {
        currentOrderId = parseInt(storedOrderId);
      }
    }

    if (!currentOrderId) {
      toast.error('Order ID not found');
      return;
    }

    if (!saleReviewData.comment.trim()) {
      toast.error('Please provide a review comment');
      return;
    }

    setSubmittingSaleReview(true);
    try {
      const ratingsArray = Object.entries(saleReviewData.ratings).map(
        ([categoryId, rating]) => ({
          sale_review_category_id: parseInt(categoryId),
          rating: rating,
        })
      );

      const payload = {
        sale_id: currentOrderId,
        customer_id: userDetails.id || 456,
        comment: saleReviewData.comment,
        is_visible: true,
        ratings: ratingsArray,
      };

      let response;

      if (existingSaleReview) {
        // Update existing review
        response = await putData(
          `sale-review/${existingSaleReview.id}`,
          payload
        );
      } else {
        // Create new review
        response = await postData('sale-review', payload);
      }

      if (response.status == 200 || response.status == 201) {
        const successMessage = existingSaleReview
          ? 'Sale review updated successfully!'
          : 'Sale review submitted successfully!';
        toast.success(successMessage);
        setShowSaleReviewModal(false);
        setSaleReviewData({
          comment: '',
          ratings: {},
        });
        // Reset ratings to default
        const defaultRatings: Record<number, number> = {};
        reviewCategories.forEach((category: any) => {
          defaultRatings[category.id] = 5;
        });
        setSaleReviewData((prev) => ({ ...prev, ratings: defaultRatings }));

        // Refresh order details to get updated review data
        await getOrderDetails();
      }
    } catch (error) {
      console.log('Sale review submission error:', error);
    } finally {
      setSubmittingSaleReview(false);
    }
  };

  const closeSaleReviewModal = () => {
    setShowSaleReviewModal(false);
  };

  const handleCategoryRating = (categoryId: number, rating: number) => {
    setSaleReviewData((prev) => ({
      ...prev,
      ratings: {
        ...prev.ratings,
        [categoryId]: rating,
      },
    }));
  };

  const toggleComboDetails = (productId: number) => {
    setExpandedCombos((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    // Store orderId in localStorage when it changes
    if (orderId) {
      localStorage.setItem('selectedOrderId', orderId.toString());
    }
    getOrderDetails();
    getReviewCategories();
  }, [orderId]);

  return (
    <div className="row">
      {loading ? (
        <Spinner />
      ) : (
        <div className="col-md-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              Order Date: <strong>{salesData?.formatted_date}</strong> | Ref.
              No.: <strong>{salesData?.reference_no}</strong>
            </div>
            <Link
              className="btn btn-sm btn-primary"
              onClick={() => {
                repeatOrder(salesData?.id || 0);
              }}
              to="#"
            >
              Repeat Order
            </Link>
          </div>
          <hr className="mt-3 mb-3" />
          <div className="cart-table table-content table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th></th>
                  <th>Product</th>
                  <th>Qty</th>
                  <th>Price</th>
                  {reviewSettings.product_review_enabled && <th>Review</th>}
                </tr>
              </thead>
              <tbody>
                {productsList?.map((product) => (
                  <>
                    <tr
                      className="sale-41"
                      style={{
                        cursor:
                          product.combo_details &&
                          product.combo_details.length > 0
                            ? 'pointer'
                            : 'default',
                        backgroundColor: expandedCombos.has(product.id)
                          ? '#f8f9fa'
                          : 'transparent',
                      }}
                      onClick={() =>
                        product.combo_details &&
                        product.combo_details.length > 0 &&
                        toggleComboDetails(product.id)
                      }
                    >
                      <td>
                        <div className="d-flex align-items-center">
                          <img
                            src={product?.image}
                            alt={product?.name}
                            width={50}
                            className="me-2"
                          />
                          {product.combo_details &&
                            product.combo_details.length > 0 && (
                              <span
                                className="badge bg-warning text-dark me-2"
                                style={{ fontSize: '10px' }}
                              >
                                COMBO
                              </span>
                            )}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="fw-bold">{product?.name}</div>
                          {product.combo_details &&
                            product.combo_details.length > 0 && (
                              <span className="ms-2">
                                {expandedCombos.has(product.id) ? (
                                  <FaChevronDown />
                                ) : (
                                  <FaChevronRight />
                                )}
                              </span>
                            )}
                        </div>
                        <span className="text-muted">
                          {product?.product_variant != null &&
                            `  (${product?.product_variant?.variant?.name})`}
                        </span>
                      </td>
                      <td>{product?.qty}</td>
                      <td>{product?.total}</td>
                      {reviewSettings.product_review_enabled && (
                        <td>
                          <button
                            className={getReviewButtonClass(product)}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReviewClick(product);
                            }}
                            title={
                              product.rating
                                ? product.rating.status === 'approved'
                                  ? 'View Approved Review'
                                  : product.rating.status === 'rejected'
                                    ? 'Review Rejected'
                                    : 'Edit Review'
                                : 'Add Review'
                            }
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            {getReviewButtonText(product)}
                          </button>
                          {product.rating && (
                            <div className="mt-1">
                              <small className="text-muted">
                                Rating: {'★'.repeat(product.rating.rating)}
                                {'☆'.repeat(5 - product.rating.rating)}
                                {product.rating.status !== 'approved' && (
                                  <span className="ms-1">
                                    ({product.rating.status})
                                  </span>
                                )}
                              </small>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>

                    {/* Combo Details Row */}
                    {product.combo_details &&
                      product.combo_details.length > 0 &&
                      expandedCombos.has(product.id) && (
                        <tr>
                          <td
                            colSpan={
                              reviewSettings.product_review_enabled ? 5 : 4
                            }
                            className="p-0"
                          >
                            <div className="combo-details p-3 bg-light border-start border-4 border-warning ms-3">
                              <h6 className="mb-3 text-warning fw-bold">
                                <i className="fas fa-box-open me-2"></i>
                                Combo Products Included:
                              </h6>
                              <div className="row">
                                {product.combo_details.map(
                                  (combo: any, index: any) => (
                                    <div key={index} className="col-md-6 mb-3">
                                      <div className="d-flex align-items-center p-2 bg-white rounded border">
                                        <img
                                          src={combo.combo_product?.image_path}
                                          alt={combo.combo_product?.name}
                                          style={{
                                            width: '50px',
                                            height: '50px',
                                            objectFit: 'cover',
                                            borderRadius: '6px',
                                            marginRight: '12px',
                                          }}
                                        />
                                        <div className="flex-grow-1">
                                          <div className="fw-bold small">
                                            {combo.combo_product_name}
                                          </div>
                                          <div className="text-muted small">
                                            Code: {combo.combo_product?.code} |
                                            Qty: {combo.quantity}
                                          </div>
                                          <div className="text-primary small fw-bold">
                                            {currencyCode}&nbsp;
                                            {parseFloat(combo.price).toFixed(
                                              2
                                            )}{' '}
                                            each
                                          </div>
                                        </div>
                                        <div className="text-end">
                                          <div className="fw-bold small">
                                            {currencyCode}&nbsp;
                                            {combo.subtotal.toFixed(2)}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>
                              <div className="mt-3 pt-2 border-top">
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="fw-bold">
                                    Total Combo Value:
                                  </span>
                                  <span className="text-primary fw-bold">
                                    {currencyCode}&nbsp;
                                    {product.combo_details
                                      .reduce(
                                        (total, combo) =>
                                          total + combo.subtotal,
                                        0
                                      )
                                      .toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                  </>
                ))}
              </tbody>
              <tbody>
                <tr>
                  <th></th>
                  <th>Shipping</th>
                  <th className="price">
                    (+) {currencyCode}
                    &nbsp;
                    {salesData?.shipping_cost == null
                      ? 0
                      : salesData?.shipping_cost}
                  </th>
                </tr>
                {salesData?.express_wash_amount != null &&
                  salesData?.express_wash_amount > 0 && (
                    <tr>
                      <th></th>
                      <th>Express Wash</th>
                      <th className="price">
                        (+) {currencyCode}
                        &nbsp;
                        {salesData?.express_wash_amount}
                      </th>
                    </tr>
                  )}
                <tr>
                  <th></th>
                  <th>Discount</th>
                  <th className="price">
                    (-) {currencyCode}
                    &nbsp;
                    {/* {salesData?.coupon_discount == null
                      ? (salesData?.order_discount ? salesData?.order_discount : 0)
                      : salesData?.coupon_discount} */}
                    {salesData?.order_discount}
                  </th>
                </tr>
                <tr>
                  <th></th>
                  <th>Total</th>
                  <th className="price">
                    {currencyCode}
                    &nbsp;
                    {salesData?.grand_total == null
                      ? 0
                      : salesData?.grand_total}
                  </th>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Sale Review Section */}
          {reviewSettings.sale_review_enabled && (
            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Review Your Experience</h5>
                <button
                  className="btn btn-primary"
                  onClick={openSaleReviewModal}
                  disabled={salesData?.status !== 'Completed'}
                >
                  {existingSaleReview ? 'Edit Review' : 'Leave a Review'}
                </button>
              </div>
              {salesData?.status !== 'Completed' && (
                <small className="text-muted d-block mb-2">
                  Order must be completed to leave a review
                </small>
              )}
            </div>
          )}
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && selectedProduct && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {selectedProduct.rating &&
                  selectedProduct.rating.status === 'approved'
                    ? 'View Review'
                    : selectedProduct.rating
                      ? 'Edit Review'
                      : 'Add Review'}{' '}
                  for {selectedProduct.name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeReviewModal}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Rating</label>
                  <div className="d-flex gap-2 star-rating">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span
                        key={star}
                        className={`star ${reviewData.rating >= star ? 'text-warning' : 'text-secondary'}`}
                        onClick={
                          selectedProduct.rating &&
                          selectedProduct.rating.status === 'approved'
                            ? undefined
                            : () =>
                                setReviewData({ ...reviewData, rating: star })
                        }
                        style={{
                          cursor:
                            selectedProduct.rating &&
                            selectedProduct.rating.status === 'approved'
                              ? 'default'
                              : 'pointer',
                          fontSize: '2rem',
                        }}
                      >
                        ★
                      </span>
                    ))}
                  </div>
                  {selectedProduct.rating &&
                    selectedProduct.rating.status === 'approved' && (
                      <small className="text-muted d-block mt-2">
                        This review has been approved and cannot be edited
                      </small>
                    )}
                </div>
                <div className="mb-3">
                  <label className="form-label">Review</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={reviewData.review}
                    onChange={
                      selectedProduct.rating &&
                      selectedProduct.rating.status === 'approved'
                        ? undefined
                        : (e) =>
                            setReviewData({
                              ...reviewData,
                              review: e.target.value,
                            })
                    }
                    placeholder="Share your experience with this product..."
                    readOnly={
                      selectedProduct.rating &&
                      selectedProduct.rating.status === 'approved'
                    }
                    style={{
                      backgroundColor:
                        selectedProduct.rating &&
                        selectedProduct.rating.status === 'approved'
                          ? '#f8f9fa'
                          : 'white',
                      cursor:
                        selectedProduct.rating &&
                        selectedProduct.rating.status === 'approved'
                          ? 'not-allowed'
                          : 'text',
                    }}
                  />
                </div>
                {selectedProduct.rating &&
                  selectedProduct.rating.created_at && (
                    <div className="mb-3">
                      <small className="text-muted">
                        Review submitted on:{' '}
                        {new Date(
                          selectedProduct.rating.created_at
                        ).toLocaleDateString()}
                      </small>
                    </div>
                  )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeReviewModal}
                >
                  {selectedProduct.rating &&
                  selectedProduct.rating.status === 'approved'
                    ? 'Close'
                    : 'Cancel'}
                </button>
                {!(
                  selectedProduct.rating &&
                  selectedProduct.rating.status === 'approved'
                ) && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={submitReview}
                    disabled={submittingReview}
                  >
                    {submittingReview
                      ? 'Submitting...'
                      : selectedProduct.rating
                        ? 'Update Review'
                        : 'Submit Review'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sale Review Modal */}
      {showSaleReviewModal && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div
              className="modal-content"
              style={{ borderRadius: '12px', border: 'none' }}
            >
              <div className="modal-body p-4">
                {/* Close button */}
                <button
                  type="button"
                  className="btn-close position-absolute"
                  onClick={closeSaleReviewModal}
                  style={{ top: '16px', right: '16px' }}
                ></button>

                {/* Title */}
                <h5 className="fw-bold mb-1">
                  Leave a review for{' '}
                  {salesData.reference_no || 'the restaurant'}
                </h5>
                <p className="text-muted mb-4" style={{ fontSize: '0.9rem' }}>
                  Your opinion matters to the restaurant and to the world!
                </p>

                {/* Category Ratings - 2 column grid */}
                <div
                  className="mb-4"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '12px 24px',
                  }}
                >
                  {reviewCategories.map((category) => (
                    <div key={category.id}>
                      <div className="mb-1" style={{ fontSize: '0.9rem' }}>
                        {category.name}
                      </div>
                      <div className="d-flex gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <span
                            key={star}
                            onClick={() =>
                              handleCategoryRating(category.id, star)
                            }
                            style={{
                              cursor: 'pointer',
                              fontSize: '1.4rem',
                              color:
                                saleReviewData.ratings[category.id] >= star
                                  ? '#f5a623'
                                  : '#d0d0d0',
                              lineHeight: 1,
                            }}
                          >
                            ★
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Comment */}
                <div className="mb-4">
                  <div className="mb-2" style={{ fontSize: '0.9rem' }}>
                    Tell us about your experience at{' '}
                    {salesData.reference_no || 'the restaurant'}
                  </div>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={saleReviewData.comment}
                    onChange={(e) =>
                      setSaleReviewData({
                        ...saleReviewData,
                        comment: e.target.value,
                      })
                    }
                    placeholder={`Leave a review of your experience at ${salesData.reference_no || 'the restaurant'}`}
                    style={{ resize: 'vertical', borderRadius: '8px' }}
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="button"
                  className="btn w-100"
                  onClick={submitSaleReview}
                  disabled={submittingSaleReview}
                  style={{
                    backgroundColor: '#222',
                    color: '#fff',
                    borderRadius: '8px',
                    padding: '12px',
                    fontWeight: '600',
                  }}
                >
                  {submittingSaleReview
                    ? 'Submitting...'
                    : 'Submit Your Review'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetails;
